import { supabase } from './supabase'

// TOTP (authenticator app) two-factor helpers. Thin wrappers over
// supabase.auth.mfa so screens never touch the SDK's factor plumbing directly.

/** Where the auth gate stashes the path it interrupted to demand a code. */
export const POST_MFA_REDIRECT_KEY = 'postMfaRedirect'

/**
 * MFA is enforced for STAFF/OWNER accounts only — customers are never
 * challenged (they also have no enrol UI). Role comes from tenant config, which
 * resolves after auth, so the gate has three answers, not two:
 *  - 'allow'     render the app
 *  - 'challenge' route to /mfa
 *  - 'wait'      role still resolving — show a spinner, decide when it lands
 * A role-detection error fails CLOSED to 'challenge': the only accounts with a
 * verified factor are (or were) operators, and asking a customer for a code
 * they enrolled is the safe wrong answer.
 */
export type MfaGateDecision = 'allow' | 'challenge' | 'wait'

export function mfaGateDecision(
  mfaRequired: boolean,
  roleStatus: 'loading' | 'resolved' | 'error' | 'no_record',
  userRole: string,
): MfaGateDecision {
  if (!mfaRequired) return 'allow'
  if (roleStatus === 'loading') return 'wait'
  if (roleStatus === 'resolved' && userRole === 'customer') return 'allow'
  return 'challenge'
}

export interface TotpFactor {
  id: string
  friendly_name?: string | null
  status: 'verified' | 'unverified'
  created_at: string
}

export interface TotpEnrollment {
  factorId: string
  /** SVG markup of the QR code to scan with an authenticator app. */
  qrCode: string
  /** The shared secret, for manual entry when the QR can't be scanned. */
  secret: string
  /**
   * otpauth:// deep link. On a phone the QR is useless — the authenticator app
   * is on the same device that shows it — so this link opens the app directly.
   */
  uri: string
}

export async function listTotpFactors(): Promise<TotpFactor[]> {
  const { data, error } = await supabase.auth.mfa.listFactors()
  if (error) throw error
  return (data?.all ?? [])
    .filter(f => f.factor_type === 'totp')
    .map(f => ({
      id: f.id,
      friendly_name: f.friendly_name,
      status: f.status,
      created_at: f.created_at,
    }))
}

/**
 * Starts TOTP enrolment. Any dangling unverified factor is removed first —
 * an abandoned enrolment otherwise blocks the next attempt with a
 * duplicate-friendly-name error.
 */
export async function enrollTotp(): Promise<TotpEnrollment> {
  const existing = await listTotpFactors()
  for (const stale of existing.filter(f => f.status === 'unverified')) {
    await supabase.auth.mfa.unenroll({ factorId: stale.id }).catch(() => {})
  }

  const { data, error } = await supabase.auth.mfa.enroll({
    factorType: 'totp',
    friendlyName: 'Authenticator app',
  })
  if (error) throw error
  return { factorId: data.id, qrCode: data.totp.qr_code, secret: data.totp.secret, uri: data.totp.uri }
}

/**
 * Verifies a 6-digit code against a factor — used both to finish enrolment and
 * to pass the sign-in challenge. On success the SDK refreshes the session to
 * aal2, which flips useAuth's mfaRequired off.
 */
export async function verifyTotp(factorId: string, code: string): Promise<void> {
  const { data: challenge, error: challengeError } = await supabase.auth.mfa.challenge({ factorId })
  if (challengeError) throw challengeError
  const { error } = await supabase.auth.mfa.verify({ factorId, challengeId: challenge.id, code })
  if (error) throw error
}

/**
 * Turns a failed challenge/verify into what to tell the person in front of the
 * screen. It exists because the screens used to report EVERY failure as
 * "Invalid code", and that hid a real outage for two weeks: a config push
 * disabled TOTP on the project, GoTrue answered every attempt with
 * `mfa_totp_verify_not_enabled` before looking at the code, and the owner sat
 * there retyping codes from an authenticator that was working perfectly.
 *
 * A wrong or stale code is the only thing the user can fix by trying again.
 * Everything else is a fault and must SAY it is a fault, and name itself — the
 * reason code is what turns "MFA is broken" into a five-minute diagnosis.
 */
export interface MfaErrorMessage {
  title: string
  description: string
  /** False when retyping the code cannot possibly help. */
  retryable: boolean
}

/** GoTrue codes that mean the six digits were wrong, stale, or already spent. */
const CODE_REJECTED = new Set([
  'mfa_verification_failed',
  'mfa_challenge_expired',
  'mfa_verification_rejected',
])

function authErrorCode(err: unknown): string {
  if (!err || typeof err !== 'object') return ''
  const rec = err as Record<string, unknown>
  const code = rec.code ?? rec.error_code
  return typeof code === 'string' ? code : ''
}

function authErrorMessage(err: unknown): string {
  if (err instanceof Error && err.message) return err.message
  if (err && typeof err === 'object') {
    const rec = err as Record<string, unknown>
    const msg = rec.msg ?? rec.message
    if (typeof msg === 'string' && msg) return msg
  }
  return ''
}

export function describeMfaError(err: unknown): MfaErrorMessage {
  const code = authErrorCode(err)
  const message = authErrorMessage(err)

  if (CODE_REJECTED.has(code) || /invalid totp code/i.test(message)) {
    return {
      title: 'Invalid code',
      description: 'That code didn’t match. Codes rotate every 30 seconds — try the current one.',
      retryable: true,
    }
  }

  if (code === 'over_request_rate_limit' || code === 'over_email_send_rate_limit') {
    return {
      title: 'Too many attempts',
      description: 'Wait a minute, then enter a fresh code.',
      retryable: true,
    }
  }

  // TOTP switched off at the project level. Nothing the user types can pass, so
  // never dress this up as a bad code — it is a server-side fix.
  if (code === 'mfa_totp_verify_not_enabled' || code === 'mfa_totp_enroll_not_enabled') {
    return {
      title: 'Two-factor is unavailable',
      description: `Your code is fine — two-factor is switched off on the server (${code}). This needs fixing in the project’s auth settings.`,
      retryable: false,
    }
  }

  return {
    title: 'Couldn’t check that code',
    description: message
      ? `${message}${code ? ` (${code})` : ''}`
      : 'Something went wrong reaching the sign-in service. This is not a problem with your code.',
    retryable: false,
  }
}

/** Removes a factor. Requires an aal2 session when the factor is verified. */
export async function unenrollTotp(factorId: string): Promise<void> {
  const { error } = await supabase.auth.mfa.unenroll({ factorId })
  if (error) throw error
}

/** Renders the enrolment QR as an <img>-safe src whatever form the SDK returns. */
export function qrCodeSrc(qrCode: string): string {
  if (qrCode.startsWith('data:')) return qrCode
  return `data:image/svg+xml;utf-8,${encodeURIComponent(qrCode)}`
}
